import CountUp from 'react-countup';
import { BarChart3 } from 'lucide-react';
import {
  translateCompletedInterviews,
  translateInterviewsInProgress,
  translateCompletionRate,
  translateStatistics,
  translateTotal,
  translateTimeToCompletion,
  translateMedianTime,
  translateAverageTime,
} from '../project_results/resultsPageTranslations';
import { StatisticsProps } from '@/types';
import StatisticCard from './StatisticCard';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import AnalysisSection from '@/components/analysis/AnalysisSection';
import { resolveTranslation } from '@/utils/resolveTranslation';

function formatTime(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: seconds.toString().padStart(2, '0'),
  };
}

function Statistics({ data, isLoading }: StatisticsProps) {
  const { currentProject: singleProjectData } = useCurrentProject();
  const analysisLanguage = singleProjectData?.analysis_language ?? 'English';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataAny = data as any;

  const avg = formatTime(data?.average_time || 0);
  const median = formatTime(dataAny?.median_time || 0);

  const statsTitle = `${resolveTranslation(translateStatistics, analysisLanguage)} (${resolveTranslation(translateTotal, analysisLanguage)})`;

  return (
    <AnalysisSection
      title={statsTitle}
      icon={<BarChart3 className="h-5 w-5" />}
      fullWidth
      defaultOpen
    >
      <div className="grid grid-cols-4 gap-3 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
        {/* completed interviews */}
        <StatisticCard
          isLoading={isLoading}
          bgColor="#059669"
          title={resolveTranslation(translateCompletedInterviews, analysisLanguage)}
        >
          <CountUp end={data?.completed_interviews ?? 0} /> /{' '}
          {singleProjectData?.respondents_max || 0}
        </StatisticCard>
        {/* started interviews */}
        <StatisticCard
          isLoading={isLoading}
          bgColor="#3b82f6"
          title={resolveTranslation(translateInterviewsInProgress, analysisLanguage)}
        >
          <CountUp end={data?.started_interviews ?? 0} />
        </StatisticCard>
        {/* completion rate */}
        <StatisticCard
          isLoading={isLoading}
          bgColor="#8b5cf6"
          title={resolveTranslation(translateCompletionRate, analysisLanguage)}
        >
          <CountUp end={data?.completion_rate ?? 0} />%
        </StatisticCard>

        {/* time to completion */}
        <StatisticCard
          isLoading={isLoading}
          bgColor="#64748b"
          title={resolveTranslation(translateTimeToCompletion, analysisLanguage)}
        >
          <div className="flex flex-col text-lg">
            <div>
              <span className="text-xs font-medium text-neutral-500">
                {resolveTranslation(translateMedianTime, analysisLanguage)}:
              </span>{' '}
              {+median.hours > 0 ? <CountUp end={+median.hours} /> : 0}h{' '}
              {+median.minutes ? <CountUp end={+median.minutes} /> : 0}m{' '}
              {+median.seconds < 10 && '0'}
              <CountUp end={Number(median.seconds)} />s
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500">
                {resolveTranslation(translateAverageTime, analysisLanguage)}:
              </span>{' '}
              {+avg.hours > 0 ? <CountUp end={+avg.hours} /> : 0}h{' '}
              {+avg.minutes ? <CountUp end={+avg.minutes} /> : 0}m {+avg.seconds < 10 && '0'}
              <CountUp end={Number(avg.seconds)} />s
            </div>
          </div>
        </StatisticCard>
      </div>
    </AnalysisSection>
  );
}

export default Statistics;
